// Pass 2.13 — search bar mounted in TopNav.centerSlot.
//
// Layout:
//   [scope chip ▾] [text input ......................... 🔍]
//
// Behaviour:
//   - "/" anywhere on the page focuses the input (Slack convention)
//     UNLESS another text field already has focus.
//   - Esc clears the query and closes the dropdown.
//   - Clicking the chip cycles through allowed scopes via a small
//     popover.
//   - Below 900px viewport, collapses to a single icon button that
//     toggles a full-width input + results panel directly below
//     the nav (rendered by SearchOverlay). The search bar component
//     itself only renders the wide form; the narrow form is handled
//     by the parent (BrowseView) so the panel can occupy the row
//     below TopNav cleanly.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from "react";
import { ChevronDown, Search } from "lucide-react";

import type { SearchScope } from "../lib/api";

interface SearchBarProps {
  query: string;
  onQueryChange: (q: string) => void;
  scope: SearchScope;
  onScopeChange: (s: SearchScope) => void;
  conversationScopeAvailable: boolean;
  /** Whether the cross-workspace ('all') scope is selectable. The
   *  search bar can always offer it, but BrowseView may want to
   *  hide it when no workspaces are loaded. Default: always
   *  available (match the spec). */
  allScopeAvailable?: boolean;
  /** Open the results dropdown. Called when the input gains focus
   *  while the query is non-empty. */
  onOpenResults: () => void;
  /** Called when the user presses Esc, clicks outside, or the
   *  parent otherwise wants the dropdown closed. */
  onCloseResults: () => void;
  /** Used by the "/" hotkey check to know whether the bar's input
   *  is currently focused (so we don't re-focus it). */
  inputId?: string;
  /** Hide the scope chip entirely (Home: scope is a page constant —
   *  "all" — so there is nothing to choose). */
  hideScope?: boolean;
}

const SCOPE_LABELS: Record<SearchScope, string> = {
  conversation: "This conversation",
  workspace: "This workspace",
  all: "All workspaces",
};

export function SearchBar({
  query,
  onQueryChange,
  scope,
  onScopeChange,
  conversationScopeAvailable,
  allScopeAvailable = true,
  onOpenResults,
  onCloseResults,
  inputId = "search-input",
  hideScope = false,
}: SearchBarProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const scopeWrapRef = useRef<HTMLDivElement | null>(null);

  // "/" global focus shortcut. Only fires when the user is not
  // already in a text-entry element.
  useEffect(() => {
    function onKey(e: globalThis.KeyboardEvent): void {
      if (e.key !== "/") return;
      const active = document.activeElement;
      const tag = active?.tagName;
      const isEditable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (isEditable) return;
      e.preventDefault();
      inputRef.current?.focus();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close scope menu on outside click.
  useEffect(() => {
    if (!scopeMenuOpen) return;
    function onClick(e: MouseEvent): void {
      const wrap = scopeWrapRef.current;
      if (wrap && !wrap.contains(e.target as Node)) {
        setScopeMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [scopeMenuOpen]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onQueryChange(e.target.value);
      if (e.target.value.length > 0) {
        onOpenResults();
      }
    },
    [onQueryChange, onOpenResults],
  );

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        // Pass 2.13.4 BUG 5: standard Esc UX — clear the query if
        // there is one (the dropdown will close as a consequence
        // of the empty query via BrowseView's effect), and ALSO
        // call onCloseResults explicitly to cover the case where
        // the dropdown is open with an empty query (e.g. the user
        // just opened it but hasn't typed yet). Blur the input
        // last so focus visibly moves away — without this, real-
        // browser users sometimes don't notice the dropdown closed
        // because the input still looks active.
        if (query.length > 0) {
          onQueryChange("");
        }
        onCloseResults();
        e.currentTarget.blur();
      }
    },
    [query, onQueryChange, onCloseResults],
  );

  const handleFocus = useCallback(() => {
    if (query.length > 0) onOpenResults();
  }, [query, onOpenResults]);

  const handlePickScope = useCallback(
    (s: SearchScope) => {
      onScopeChange(s);
      setScopeMenuOpen(false);
    },
    [onScopeChange],
  );

  return (
    <div
      data-testid="search-bar"
      className="flex items-center gap-2 w-[460px] max-w-full rounded-full bg-white/[0.05] border border-white/[0.1] focus-within:border-theme-accent-link/60 transition-colors duration-150 py-0.5"
    >
      {!hideScope && (
      <div ref={scopeWrapRef} className="relative">
        <button
          type="button"
          onClick={() => setScopeMenuOpen((s) => !s)}
          data-testid="search-scope-chip"
          aria-haspopup="listbox"
          aria-expanded={scopeMenuOpen}
          className="inline-flex items-center gap-1 pl-3.5 pr-2 py-1.5 font-mono text-[10px] uppercase tracking-widest text-theme-text-secondary hover:text-theme-text-primary transition-colors duration-150 border-r border-white/[0.08]"
        >
          <span data-testid="search-scope-label">{SCOPE_LABELS[scope]}</span>
          <ChevronDown className="w-3 h-3" strokeWidth={1.5} aria-hidden />
        </button>
        {scopeMenuOpen && (
          <div
            data-testid="search-scope-menu"
            className="absolute left-0 top-full mt-1 z-50 w-44 rounded-xl overflow-hidden bg-theme-bg-card border border-white/[0.1] shadow-2xl"
            role="listbox"
          >
            <ScopeOption
              value="conversation"
              current={scope}
              disabled={!conversationScopeAvailable}
              onPick={handlePickScope}
            />
            <ScopeOption
              value="workspace"
              current={scope}
              disabled={false}
              onPick={handlePickScope}
            />
            <ScopeOption
              value="all"
              current={scope}
              disabled={!allScopeAvailable}
              onPick={handlePickScope}
            />
          </div>
        )}
      </div>
      )}
      <input
        ref={inputRef}
        id={inputId}
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKey}
        onFocus={handleFocus}
        placeholder="Search messages…"
        data-testid="search-input"
        autoComplete="off"
        className="flex-1 bg-transparent border-none outline-none px-2 py-1.5 font-mono text-[12px] text-theme-text-primary placeholder:text-theme-text-tertiary"
      />
      <Search
        className="w-3.5 h-3.5 text-theme-text-tertiary mr-3 flex-shrink-0"
        strokeWidth={1.5}
        aria-hidden
      />
    </div>
  );
}

interface ScopeOptionProps {
  value: SearchScope;
  current: SearchScope;
  disabled: boolean;
  onPick: (s: SearchScope) => void;
}

function ScopeOption({ value, current, disabled, onPick }: ScopeOptionProps) {
  const isActive = current === value;
  return (
    <button
      type="button"
      role="option"
      aria-selected={isActive}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={() => onPick(value)}
      data-testid={`search-scope-option-${value}`}
      title={
        disabled && value === "conversation"
          ? "Select a conversation to scope search to it"
          : undefined
      }
      className={[
        "w-full text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors duration-150",
        isActive
          ? "bg-theme-bg-conv-active text-theme-accent-text"
          : disabled
            ? "text-theme-text-tertiary opacity-50 cursor-not-allowed"
            : "text-theme-text-secondary hover:bg-theme-bg-conv-hover hover:text-theme-text-primary",
      ].join(" ")}
    >
      {SCOPE_LABELS[value]}
    </button>
  );
}
